import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useCity } from '../../contexts/CityContext';
import { useDateFilter } from '../../contexts/DateFilterContext';
import config from '../../config';
import StartConversationModal from '../customers/StartConversationModal';
import { getCustomerDisplayName } from '../../utils/customerUtils';
import './EscalationDashboard.css';

const EscalationDashboard = () => {
  const location = useLocation();
  const { selectedCities } = useCity();
  const { isDateInRange } = useDateFilter();
  const [unassignedBookings, setUnassignedBookings] = useState([]);
  const [hourlyUnassignedBookings, setHourlyUnassignedBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('regular'); // regular | hourly
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [lastUpdate, setLastUpdate] = useState(null);
  const websocketRef = useRef(null);

  // Manual assignment modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [assignMode, setAssignMode] = useState('regular'); // regular | hourly
  const [vehicleId, setVehicleId] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  // NEW: Validation state for two-step flow
  const [validationStep, setValidationStep] = useState('input'); // 'input' | 'warnings' | 'confirmed'
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [warningsAcknowledged, setWarningsAcknowledged] = useState(false);

  const [opsRecommendations, setOpsRecommendations] = useState([]);
  const [rejectedDrivers, setRejectedDrivers] = useState({});  // booking_id -> rejected driver info
  const [firstRejectedDrivers, setFirstRejectedDrivers] = useState({});  // booking_id -> first rejected driver info
  const [zoneDrivers, setZoneDrivers] = useState({});  // booking_id -> [{driver_id, driver_name, vehicle_class, distance_km}]
  const [customerInfo, setCustomerInfo] = useState({});  // booking_id -> {customer_name, customer_business_name, customer_phone}
  const [expandedZoneDrivers, setExpandedZoneDrivers] = useState({});  // booking_id -> true/false for collapse toggle
  const [rejectedDriverToggle, setRejectedDriverToggle] = useState({});  // booking_id -> 'first' | 'last'

  // CONTACT CUSTOMER: Modal state
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactBookingId, setContactBookingId] = useState(null);
  const [contactCustomerId, setContactCustomerId] = useState(null);
  const [contactCustomerPhone, setContactCustomerPhone] = useState(null);
  const [showConversationModal, setShowConversationModal] = useState(false);

  // Helper function to normalize city names for matching
  // Handles variations like "Karkala" vs "Karkal", "Mangalore" vs "Mangaluru"
  const normalizeCityName = (cityName) => {
    return cityName.toLowerCase()
      .replace(/\s+/g, '') // Remove spaces
      .replace(/uru$/i, '') // Remove 'uru' suffix (Mangaluru -> Mangal)
      .replace(/ur$/i, '')  // Remove 'ur' suffix  
      .replace(/a$/i, '');  // Remove 'a' suffix (Karkala -> Karkal)
  };

  // Check if a location matches any of the selected cities
  const locationMatchesCities = (location, cities) => {
    if (!location || !cities || cities.length === 0) return true;

    const locationNormalized = normalizeCityName(location);

    return cities.some(city => {
      const cityNormalized = normalizeCityName(city);
      // Check if either contains the other (handles partial matches)
      return locationNormalized.includes(cityNormalized) ||
        cityNormalized.includes(locationNormalized) ||
        location.toLowerCase().includes(city.toLowerCase());
    });
  };

  useEffect(() => {
    let websocket = null;
    let reconnectTimeout = null;
    let isComponentMounted = true;
    let hasConnected = false;

    // WebSocket connection to receive escalations
    const connectWebSocket = () => {
      if (!isComponentMounted || hasConnected) return;
      hasConnected = true;

      // Close existing connection if any
      if (websocket) {
        console.log('🔌 Closing existing WebSocket connection');
        websocket.close();
      }

      const connectionId = Math.random().toString(36).substr(2, 9);
      console.log(`🔌 Creating new WebSocket connection: ${connectionId}`);
      websocket = new WebSocket(config.escalationWebsocket);
      websocketRef.current = websocket;

      websocket.onopen = () => {
        if (!isComponentMounted) return;
        console.log(`✅ Connected to escalation WebSocket: ${connectionId}`);
        setConnectionStatus('Connected');
        // Clear any pending reconnection attempts
        if (reconnectTimeout) {
          clearTimeout(reconnectTimeout);
          reconnectTimeout = null;
        }
      };

      websocket.onmessage = (event) => {
        if (!isComponentMounted) return;
        const data = JSON.parse(event.data);
        console.log('📥 Received escalation:', data);
        console.log('📊 Unassigned count:', data.unassigned?.length);

        if (data.type === 'escalation' && data.unassigned && data.unassigned.length > 0) {
          // Replace with fresh data each time (don't accumulate)
          console.log('📌 Previous bookings:', unassignedBookings.length);
          console.log('🔄 Replacing with fresh data:', data.unassigned.length);
          setUnassignedBookings(data.unassigned);
          setLastUpdate(new Date(data.timestamp));
        } else if (data.type === 'clear_all') {
          // Clear all escalations command from server
          console.log('🧹 Received clear all command from server');
          setUnassignedBookings([]);
          setLastUpdate(new Date(data.timestamp));
        } else if (data.type === 'ops_recommendation') {
          // OPS AGENT: Handle recommendation messages
          console.log('🤖 Ops Agent recommendations:', data.recommendations?.length);
          setOpsRecommendations(data.recommendations || []);
          // Store rejected driver info
          if (data.rejected_drivers) {
            console.log('🚫 Rejected drivers:', Object.keys(data.rejected_drivers).length);
            setRejectedDrivers(data.rejected_drivers);
          }
          // Store first rejected driver info
          if (data.first_rejected_drivers) {
            console.log('🚫 First rejected drivers:', Object.keys(data.first_rejected_drivers).length);
            setFirstRejectedDrivers(data.first_rejected_drivers);
          }
          // Store zone drivers info
          if (data.zone_drivers) {
            console.log('🚗 Zone drivers:', Object.keys(data.zone_drivers).length);
            setZoneDrivers(data.zone_drivers);
          }
          // Store customer info
          if (data.customer_info) {
            setCustomerInfo(data.customer_info);
          }
        }
      };

      websocket.onclose = (event) => {
        if (!isComponentMounted) return;
        console.log('❌ WebSocket connection closed', event.code, event.reason);
        setConnectionStatus('Disconnected');

        // Only attempt to reconnect if the component is still mounted and connection wasn't closed intentionally
        if (isComponentMounted && event.code !== 1000) {
          hasConnected = false; // Reset flag to allow reconnection
          reconnectTimeout = setTimeout(() => {
            if (isComponentMounted) {
              console.log('🔄 Attempting to reconnect...');
              connectWebSocket();
            }
          }, 3000);
        }
      };

      websocket.onerror = (error) => {
        if (!isComponentMounted) return;
        console.error('WebSocket error:', error);
        setConnectionStatus('Error');
      };
    };

    connectWebSocket();

    // Cleanup on component unmount
    return () => {
      isComponentMounted = false;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (websocket) {
        websocket.close(1000, 'Component unmounting');
      }
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchHourlyUnassigned = async () => {
      try {
        const params = new URLSearchParams();
        if (selectedCities && selectedCities.length > 0) {
          params.append('cities', selectedCities.join(','));
        }

        const query = params.toString();
        const response = await fetch(`${config.opsApiBase}/hourly-unassigned-bookings${query ? `?${query}` : ''}`);
        const data = await response.json();

        if (!isMounted) return;
        if (response.ok && data.success) {
          setHourlyUnassignedBookings(data.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch hourly unassigned bookings:', err);
      }
    };

    fetchHourlyUnassigned();
    const timer = setInterval(fetchHourlyUnassigned, 10000);

    return () => {
      isMounted = false;
      clearInterval(timer);
    };
  }, [selectedCities]);

  const getUrgencyLevel = (booking) => {
    // Use the priority sent from backend if available
    if (booking.priority) {
      if (booking.priority.includes('CRITICAL')) return 'critical';
      if (booking.priority.includes('MEDIUM')) return 'high';
      if (booking.priority.includes('LOW')) return 'medium';
    }

    // Fallback to time-based calculation if no priority field
    const pickup = new Date(booking.pickup_time);
    const now = new Date();
    const hoursUntilPickup = (pickup - now) / (1000 * 60 * 60);

    if (hoursUntilPickup <= 2) return 'critical';  // Match backend: ≤2h = critical
    if (hoursUntilPickup <= 3) return 'high';     // Match backend: ≤3h = medium->high  
    return 'medium';                               // Match backend: >3h = low->medium
  };

  // Sort and filter bookings with useMemo - ALWAYS maintains priority order
  const filteredBookings = useMemo(() => {
    // First, filter by city using smart matching
    let filtered;
    if (selectedCities && selectedCities.length > 0) {
      filtered = unassignedBookings.filter(booking => {
        const pickupLocation = booking.pickup_location || '';
        const dropoffLocation = booking.dropoff_location || '';

        // Match if either pickup or drop location matches any selected city
        return locationMatchesCities(pickupLocation, selectedCities) ||
          locationMatchesCities(dropoffLocation, selectedCities);
      });
    } else {
      filtered = [...unassignedBookings]; // Create a copy to avoid mutating original
    }

    // Apply date filter - check pickup_time or created_at
    filtered = filtered.filter(booking => {
      const bookingDate = booking.pickup_time || booking.created_at;
      return !bookingDate || isDateInRange(bookingDate);
    });

    // Then, sort by urgency level: critical -> high -> medium
    const priorityOrder = { critical: 0, high: 1, medium: 2 };

    return filtered.sort((a, b) => {
      const urgencyA = getUrgencyLevel(a);
      const urgencyB = getUrgencyLevel(b);

      // Sort by priority (lower number = higher priority)
      return priorityOrder[urgencyA] - priorityOrder[urgencyB];
    });
  }, [unassignedBookings, selectedCities, isDateInRange, locationMatchesCities]);

  const filteredHourlyBookings = useMemo(() => {
    let filtered = [...hourlyUnassignedBookings];

    // Apply date filter
    filtered = filtered.filter(booking => {
      const bookingDate = booking.pickup_time || booking.last_failed_at;
      return !bookingDate || isDateInRange(bookingDate);
    });

    // Sort by pickup time asc
    return filtered.sort((a, b) => {
      const ta = new Date(a.pickup_time || 0).getTime();
      const tb = new Date(b.pickup_time || 0).getTime();
      return ta - tb;
    });
  }, [hourlyUnassignedBookings, isDateInRange]);

  // Auto-open manual assignment modal if redirected from TripDetails
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const assignBookingId = queryParams.get('assignBooking');

    if (assignBookingId) {
      console.log('📍 Auto-opening manual assignment modal for booking:', assignBookingId);

      // Wait a bit for bookings to load from WebSocket
      const timer = setTimeout(() => {
        const bookingExists = unassignedBookings.some(
          booking => booking.booking_id === parseInt(assignBookingId)
        );

        if (bookingExists) {
          setSelectedBookingId(parseInt(assignBookingId));
          setAssignMode('regular');
          setVehicleId('');
          setShowAssignModal(true);

          // Scroll to the booking in the list
          const detailsRow = document.getElementById(`details-${assignBookingId}`);
          if (detailsRow) {
            detailsRow.style.display = 'table-row';
            detailsRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } else {
          console.warn('⚠️ Booking not found in unassigned list:', assignBookingId);
          alert(`Booking #${assignBookingId} is not in the unassigned list. It may have already been assigned or cancelled.`);
        }
      }, 1000); // Wait 1 second for WebSocket data

      return () => clearTimeout(timer);
    }
  }, [location.search, unassignedBookings]);

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString();
  };

  const handleAssignManually = (bookingId, mode = 'regular') => {
    setSelectedBookingId(bookingId);
    setAssignMode(mode);
    setVehicleId('');
    setShowAssignModal(true);
  };

  const handleContactCustomer = (bookingId, customerId, customerPhone) => {
    console.log(`Contact customer for booking ${bookingId}, customer ${customerId}, phone ${customerPhone}`);
    setContactBookingId(bookingId);
    setContactCustomerId(customerId);
    setContactCustomerPhone(customerPhone);
    setShowContactModal(true);
  };

  const handleCallCustomer = () => {
    if (contactCustomerPhone) {
      window.open(`tel:${contactCustomerPhone}`);
    } else {
      alert('Customer phone number not available');
    }
  };

  const handleTextCustomer = () => {
    setShowContactModal(false);
    setShowConversationModal(true);
  };

  const handleCloseContactModal = () => {
    setShowContactModal(false);
    setContactBookingId(null);
    setContactCustomerId(null);
    setContactCustomerPhone(null);
  };

  const handleClearAll = () => {
    // Send clear command to server so all clients get cleared
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
      const clearMessage = {
        type: 'clear_escalations',
        timestamp: new Date().toISOString()
      };
      websocketRef.current.send(JSON.stringify(clearMessage));
      console.log('🧹 Sent clear all command to server');
    } else {
      // Fallback - clear locally if no connection
      setUnassignedBookings([]);
      console.log('🧹 Cleared escalation bookings locally');
    }
  };

  const handleRemoveBooking = async (bookingId) => {
    // Remove from local state immediately for responsiveness
    setUnassignedBookings(prev => prev.filter(booking => booking.booking_id !== bookingId));

    // Also notify ws_server so the booking is added to dismissed set
    // and won't reappear in the next realtimeassigner cycle
    try {
      await fetch(`${config.wsServerApiBase}/broadcast/remove-booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: bookingId, reason: 'manual_remove' }),
      });
      console.log(`✅ Booking ${bookingId} dismissed on server`);
    } catch (error) {
      console.warn(`⚠️ Failed to dismiss booking ${bookingId} on server:`, error);
      // Local removal still works, but it may reappear in next cycle
    }
  };

  // NEW: Step 1 - Validate assignment before proceeding
  const handleValidateAssignment = async () => {
    if (!vehicleId.trim()) {
      alert('Please enter a vehicle ID');
      return;
    }

    // Hourly tab: assign immediately via FastAPI (no websocket / no warning-step)
    if (assignMode === 'hourly') {
      setIsAssigning(true);
      try {
        const operatorName = localStorage.getItem('user_name') || localStorage.getItem('username') || 'Unknown Operator';
        const response = await fetch(`${config.opsApiBase}/hourly-unassigned-bookings/assign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            booking_id: selectedBookingId,
            vehicle_id: parseInt(vehicleId.trim(), 10),
            assigned_by: operatorName,
            source: 'unassigned_page',
          }),
        });

        const result = await response.json();
        if (response.ok && result.success) {
          setHourlyUnassignedBookings(prev => prev.filter(b => b.booking_id !== selectedBookingId));
          handleCancelAssignment();
          alert(`Successfully assigned hourly booking ${selectedBookingId} to vehicle ${vehicleId}`);
        } else {
          alert(`Assignment failed: ${result.detail || result.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error during hourly assignment:', error);
        alert(`Error during assignment: ${error.message}`);
      } finally {
        setIsAssigning(false);
      }
      return;
    }

    setIsValidating(true);

    try {
      const response = await fetch(`${config.dispatchApiBase}/manual-assignment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          booking_id: selectedBookingId,
          vehicle_id: parseInt(vehicleId.trim()),
          check_only: true  // Step 1: Validation only
        }),
      });

      const result = await response.json();
      console.log('Validation result:', result);

      setValidationResult(result);

      if (!result.can_proceed) {
        // Hard block - show error, don't allow proceeding
        setValidationStep('warnings');
      } else if (result.warnings && result.warnings.length > 0) {
        // Has warnings - show them for acknowledgment
        setValidationStep('warnings');
      } else {
        // No issues - proceed directly
        await handleConfirmAssignment(result.validation_token);
      }
    } catch (error) {
      console.error('Error during validation:', error);
      alert(`Error during validation: ${error.message}`);
    } finally {
      setIsValidating(false);
    }
  };

  // NEW: Step 2 - Confirm assignment after warnings acknowledged
  const handleConfirmAssignment = async (validationToken = null) => {
    setIsAssigning(true);

    try {
      const operatorName = localStorage.getItem('user_name') || localStorage.getItem('username') || 'Unknown Operator';

      const response = await fetch(`${config.dispatchApiBase}/manual-assignment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          booking_id: selectedBookingId,
          vehicle_id: parseInt(vehicleId.trim()),
          assigned_by: operatorName,
          source: 'unassigned_page',
          confirmed: true,  // Step 2: Confirmed assignment
          validation_token: validationToken || validationResult?.validation_token,
          warnings_acknowledged: warningsAcknowledged
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Assignment successful:', result);

        // Remove the assigned booking from the unassigned list
        setUnassignedBookings(prev =>
          prev.filter(booking => booking.booking_id !== selectedBookingId)
        );

        // Also dismiss on ws_server so it doesn't reappear in next cycle
        // (api_server already does this, but belt-and-suspenders)
        try {
          await fetch(`${config.wsServerApiBase}/broadcast/remove-booking`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              booking_id: selectedBookingId,
              vehicle_id: parseInt(vehicleId.trim()),
              reason: 'manual_assignment'
            }),
          });
        } catch (wsError) {
          console.warn('⚠️ ws_server dismiss failed (non-critical):', wsError);
        }

        // Close modal and reset state
        handleCancelAssignment();

        alert(`Successfully assigned booking ${selectedBookingId} to vehicle ${vehicleId}`);
      } else if (response.status === 409) {
        // Race condition - state changed
        const error = await response.json();
        alert(`⚠️ State changed: ${error.detail}\n\nPlease close and try again.`);
        // Reset to input step
        setValidationStep('input');
        setValidationResult(null);
      } else {
        const error = await response.json();
        console.error('Assignment failed:', error);
        alert(`Assignment failed: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error during assignment:', error);
      alert(`Error during assignment: ${error.message}`);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleCancelAssignment = () => {
    setShowAssignModal(false);
    setSelectedBookingId(null);
    setAssignMode('regular');
    setVehicleId('');
    setValidationStep('input');
    setValidationResult(null);
    setWarningsAcknowledged(false);
  };

  // Helper function to get severity color
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#dc3545'; // red
      case 'high': return '#fd7e14'; // orange
      case 'medium': return '#ffc107'; // yellow
      case 'low': return '#6c757d'; // gray
      default: return '#6c757d';
    }
  };

  // Helper function to get severity background color
  const getSeverityBgColor = (severity) => {
    switch (severity) {
      case 'critical': return '#f8d7da'; // light red
      case 'high': return '#fff3cd'; // light orange
      case 'medium': return '#fff9e6'; // light yellow
      case 'low': return '#e9ecef'; // light gray
      default: return '#e9ecef';
    }
  };

  const currentTotal = activeTab === 'hourly' ? filteredHourlyBookings.length : filteredBookings.length;

  return (
    <div className="escalation-dashboard">

      <div className="dashboard-header">
        <h1>🚨 Unassigned Bookings</h1>
        <div className="connection-status">
          <div className={`status-indicator ${connectionStatus.toLowerCase()}`}></div>
          <span>WebSocket: {connectionStatus}</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button
          onClick={() => setActiveTab('regular')}
          style={{
            padding: '8px 14px',
            borderRadius: '6px',
            border: activeTab === 'regular' ? '1px solid #007bff' : '1px solid #ddd',
            backgroundColor: activeTab === 'regular' ? '#e7f1ff' : '#fff',
            color: activeTab === 'regular' ? '#007bff' : '#333',
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          Regular Unassigned ({filteredBookings.length})
        </button>
        <button
          onClick={() => setActiveTab('hourly')}
          style={{
            padding: '8px 14px',
            borderRadius: '6px',
            border: activeTab === 'hourly' ? '1px solid #f57c00' : '1px solid #ddd',
            backgroundColor: activeTab === 'hourly' ? '#fff3e0' : '#fff',
            color: activeTab === 'hourly' ? '#ef6c00' : '#333',
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          Hourly Unassigned ({filteredHourlyBookings.length})
        </button>
      </div>

      <div className="dashboard-stats">
        {activeTab === 'regular' ? (
          <>
            <div className="stat-card critical">
              <h3>Critical (≤2h)</h3>
              <div className="stat-number">
                {filteredBookings.filter(b => getUrgencyLevel(b) === 'critical').length}
              </div>
            </div>
            <div className="stat-card high">
              <h3>Medium (≤3h)</h3>
              <div className="stat-number">
                {filteredBookings.filter(b => getUrgencyLevel(b) === 'high').length}
              </div>
            </div>
            <div className="stat-card medium">
              <h3>Low Priority (&gt;3h)</h3>
              <div className="stat-number">
                {filteredBookings.filter(b => getUrgencyLevel(b) === 'medium').length}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="stat-card critical">
              <h3>Pickup ≤ 30 min</h3>
              <div className="stat-number">
                {filteredHourlyBookings.filter(b => Number(b.minutes_until_pickup) <= 30).length}
              </div>
            </div>
            <div className="stat-card high">
              <h3>Pickup ≤ 60 min</h3>
              <div className="stat-number">
                {filteredHourlyBookings.filter(b => Number(b.minutes_until_pickup) > 30 && Number(b.minutes_until_pickup) <= 60).length}
              </div>
            </div>
            <div className="stat-card medium">
              <h3>Pickup ≤ 120 min</h3>
              <div className="stat-number">
                {filteredHourlyBookings.filter(b => Number(b.minutes_until_pickup) > 60).length}
              </div>
            </div>
          </>
        )}
        <div className="stat-card total">
          <h3>Total Unassigned</h3>
          <div className="stat-number">{currentTotal}</div>
        </div>
      </div>

      {activeTab === 'regular' && lastUpdate && (
        <div className="last-update">
          Last Update: {lastUpdate.toLocaleString()}
          {filteredBookings.length > 0 && (
            <button
              className="btn-clear-all"
              onClick={handleClearAll}
              style={{ marginLeft: '20px', padding: '8px 16px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              Clear All
            </button>
          )}
        </div>
      )}
      {activeTab === 'hourly' && (
        <div className="last-update">
          Hourly unassigned data refreshes every 10 seconds via FastAPI.
        </div>
      )}

      <div className="bookings-container">
        {activeTab === 'hourly' ? (
          filteredHourlyBookings.length === 0 ? (
            <div className="no-bookings">
              <h3>✅ No Hourly Unassigned Bookings</h3>
              <p>No near-term hourly bookings are waiting for manual assignment.</p>
            </div>
          ) : (
            <div className="bookings-table-container">
              <table className="bookings-table">
                <thead>
                  <tr>
                    <th>Booking ID</th>
                    <th>Pickup Location</th>
                    <th>Drop Location</th>
                    <th>Vehicle Type</th>
                    <th>Pickup Time</th>
                    <th>Minutes to Pickup</th>
                    <th>Failure Reason</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHourlyBookings.map((booking) => (
                    <tr key={booking.booking_id} className="booking-row high">
                      <td className="booking-id-cell">#{booking.booking_id}</td>
                      <td className="location-cell">
                        <span className="location-icon">📍</span>
                        {booking.pickup_location || '-'}
                      </td>
                      <td className="location-cell">
                        <span className="location-icon">🎯</span>
                        {booking.drop_location || '-'}
                      </td>
                      <td>{booking.vehicle_type || '-'}</td>
                      <td>{booking.pickup_time ? new Date(booking.pickup_time).toLocaleString() : '-'}</td>
                      <td>{Number.isFinite(Number(booking.minutes_until_pickup)) ? `${Math.max(0, Math.round(Number(booking.minutes_until_pickup)))} min` : '-'}</td>
                      <td>{booking.failure_reason || booking.last_error || '-'}</td>
                      <td className="actions-cell">
                        <button
                          className="btn-assign"
                          onClick={() => handleAssignManually(booking.booking_id, 'hourly')}
                        >
                          Assign Vehicle
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          filteredBookings.length === 0 ? (
            <div className="no-bookings">
              <h3>✅ No Unassigned Bookings</h3>
              <p>All bookings are currently assigned to vehicles.</p>
            </div>
          ) : (
            <div className="bookings-table-container">
              <table className="bookings-table">
              <thead>
                <tr>
                  <th>Priority</th>
                  <th>Booking ID</th>
                  <th>Pickup Location</th>
                  <th>Drop Location</th>
                  <th>Vehicle Type</th>
                  <th>Distance</th>
                  <th>Pickup Time</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                  <th style={{ textAlign: 'center' }}>Agent</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map((booking) => {
                  const urgencyLevel = getUrgencyLevel(booking);
                  return (
                    <React.Fragment key={booking.booking_id}>
                      <tr className={`booking-row ${urgencyLevel}`}>
                        <td>
                          <span className={`urgency-badge ${urgencyLevel}`}>
                            {urgencyLevel.toUpperCase()}
                          </span>
                        </td>
                        <td className="booking-id-cell">#{booking.booking_id}</td>
                        <td className="location-cell">
                          <span className="location-icon">📍</span>
                          {booking.pickup_location}
                        </td>
                        <td className="location-cell">
                          <span className="location-icon">🎯</span>
                          {booking.dropoff_location}
                        </td>
                        <td>{booking.vehicle_type}</td>
                        <td>{booking.distance_km} km</td>
                        <td>{formatTime(booking.pickup_time)}</td>
                        <td className="actions-cell">
                          <button
                            className="btn-view-details"
                            onClick={() => {
                              const row = document.getElementById(`details-${booking.booking_id}`);
                              const opsRow = document.getElementById(`ops-details-${booking.booking_id}`);
                              if (opsRow) opsRow.style.display = 'none';
                              row.style.display = row.style.display === 'none' ? 'table-row' : 'none';
                            }}
                          >
                            View Details
                          </button>
                        </td>
                        <td className="actions-cell">
                          {(() => {
                            const hasRec = opsRecommendations.some(r => r.booking_id === booking.booking_id);
                            const hasRejected = !!rejectedDrivers[booking.booking_id];
                            const hasZoneDrivers = zoneDrivers[booking.booking_id] && zoneDrivers[booking.booking_id].length > 0;
                            const hasData = hasRec || hasRejected || hasZoneDrivers;

                            if (!hasData) {
                              return <span style={{ color: '#ccc', fontSize: '0.9rem' }}>—</span>;
                            }

                            const rec = hasRec ? opsRecommendations.find(r => r.booking_id === booking.booking_id) : null;
                            const isCustomerSide = rec?.type === 'customer_side';
                            
                            // Color logic: green for all Ops Agent buttons
                            const bgGradient = 'linear-gradient(135deg, #66bb6a 0%, #43a047 100%)';
                            const boxShadowColor = '0 2px 4px rgba(76, 175, 80, 0.3)';
                            const hoverShadow = '0 4px 8px rgba(76, 175, 80, 0.4)';

                            return (
                              <button
                                className="btn-ops-agent"
                                style={{
                                  background: bgGradient,
                                  boxShadow: boxShadowColor,
                                  padding: '4px 12px',
                                  fontSize: '0.85em'
                                }}
                                onClick={() => {
                                  const opsRow = document.getElementById(`ops-details-${booking.booking_id}`);
                                  const detailsRow = document.getElementById(`details-${booking.booking_id}`);
                                  if (detailsRow) detailsRow.style.display = 'none';
                                  if (opsRow) opsRow.style.display = opsRow.style.display === 'none' ? 'table-row' : 'none';
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.boxShadow = hoverShadow;
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = boxShadowColor;
                                }}
                              >
                                🤖 Ops Agent
                              </button>
                            );
                          })()}
                        </td>
                      </tr>
                      {/* View Details expanded row */}
                      <tr id={`details-${booking.booking_id}`} className="details-row" style={{ display: 'none' }}>
                        <td colSpan="9">
                          <div className="details-container">
                            <div className="details-content">
                              <div className="detail-section">
                                <h4>📋 Booking Information</h4>
                                <p><strong>Booking ID:</strong> #{booking.booking_id}</p>
                                <p><strong>Vehicle Type:</strong> {booking.vehicle_type}</p>
                                <p><strong>Distance:</strong> {booking.distance_km} km</p>
                                <p><strong>Pickup Time:</strong> {new Date(booking.pickup_time).toLocaleString()}</p>
                              </div>
                              <div className="detail-section">
                                <h4>📍 Route Details</h4>
                                <p><strong>From:</strong> {booking.pickup_location}</p>
                                <p><strong>To:</strong> {booking.dropoff_location}</p>
                              </div>
                              </div>
                            <div className="details-actions">
                              <button
                                className="btn-assign"
                                onClick={() => handleAssignManually(booking.booking_id)}
                              >
                                Assign Manually
                              </button>
                              <button
                                className="btn-contact"
                                onClick={() => handleContactCustomer(
                                  booking.booking_id,
                                  booking.customer_id,
                                  booking.customer_phone
                                )}
                              >
                                Contact Customer
                              </button>
                              <button
                                className="btn-remove"
                                onClick={() => handleRemoveBooking(booking.booking_id)}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      {/* Ops Agent expanded row — rendered if recommendation OR rejected/zone data exists */}
                      {(opsRecommendations.some(r => r.booking_id === booking.booking_id) || rejectedDrivers[booking.booking_id] || (zoneDrivers[booking.booking_id] && zoneDrivers[booking.booking_id].length > 0)) && (
                        <tr id={`ops-details-${booking.booking_id}`} className="details-row ops-details-row" style={{ display: 'none' }}>
                          <td colSpan="9">
                            {(() => {
                              const recs = opsRecommendations.filter(r => r.booking_id === booking.booking_id);
                              const hasRecs = recs.length > 0;
                              const hasRejected = !!rejectedDrivers[booking.booking_id];
                              const hasFirstRejected = !!firstRejectedDrivers[booking.booking_id];
                              const hasZone = zoneDrivers[booking.booking_id] && zoneDrivers[booking.booking_id].length > 0;
                              const toggleValue = rejectedDriverToggle[booking.booking_id] || 'last';
                              const currentRejected = toggleValue === 'first' ? firstRejectedDrivers[booking.booking_id] : rejectedDrivers[booking.booking_id];

                              return (
                                <div style={{ padding: 0 }}>
                                  {/* If there are ops recommendations, show them as before */}
                                  {hasRecs && recs.map((rec, idx) => {
                                    console.log('📋 OPS REC DEBUG:', rec.booking_id, 'type=', rec.type, 'keys=', Object.keys(rec));
                                    const isMultiple = recs.length > 1;
                                    const cardId = `ops-card-${rec.booking_id}-${rec.driver_id}-${idx}`;
                                    const expandedState = isMultiple ? 'none' : 'flex';

                                    return (
                                      <div key={cardId} style={{
                                        borderBottom: idx < recs.length - 1 ? '1px solid #e0e0e0' : 'none'
                                      }}>
                                        {isMultiple && (
                                          <div
                                            style={{
                                              padding: '12px 24px',
                                              backgroundColor: rec.profitable === false ? '#ffcdd2' : '#eef2f6',
                                              cursor: 'pointer',
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                              alignItems: 'center',
                                              borderBottom: '1px solid #d0d7de'
                                            }}
                                            onClick={() => {
                                              const el = document.getElementById(cardId);
                                              if (el) el.style.display = el.style.display === 'none' ? 'flex' : 'none';
                                            }}
                                          >
                                            <strong>{rec.profitable === false ? '🔴' : '🚗'} Vehicle: #{rec.vehicle_id}</strong>
                                            <span style={{ fontSize: '0.85em', color: rec.profitable === false ? '#c62828' : '#555' }}>
                                              {rec.margin_pct >= 0 ? '+' : ''}{rec.margin_pct}% Margin {rec.profitable === false ? '❌' : ''} (Click to Expand ▼)
                                            </span>
                                          </div>
                                        )}

                                        <div id={cardId} className={`details-container ${rec.type === 'no_drivers_available' ? 'ops-details-container-nodrivers' : rec.type === 'customer_side' ? 'ops-details-container-customer' : 'ops-details-container'}`} style={{ display: expandedState, borderTop: 'none', ...(rec.profitable === false ? { backgroundColor: '#ffebee', borderLeft: '4px solid #e53935' } : {}) }}>
                                          <div className="details-content">
                                            {rec.type === 'no_drivers_available' ? (
                                              rec.booking_type === 'scheduled' ? (
                                                <>
                                                  <div className="detail-section">
                                                    <h4 style={{ color: '#e65100', marginBottom: '8px' }}>🟠 Scheduled Booking (Driver Shortage)</h4>
                                                    <p style={{ marginBottom: '12px', fontSize: '0.95em', color: '#555' }}>
                                                      This is a scheduled booking. Currently, no driver is available. Please onboard a new vehicle.
                                                    </p>
                                                    <p><strong>Booking Time:</strong> {new Date(rec.pickup_time).toLocaleString()}</p>
                                                    <p><strong>Trip Distance:</strong> {rec.distance} km</p>
                                                  </div>
                                                  <div className="detail-section">
                                                    <h4 style={{ color: '#2e7d32', marginBottom: '8px' }}>👤 Customer Details</h4>
                                                    {customerInfo[booking.booking_id] ? (
                                                      <>
                                                        <p><strong>Name:</strong> {getCustomerDisplayName(customerInfo[booking.booking_id].customer_name, customerInfo[booking.booking_id].customer_business_name, customerInfo[booking.booking_id].customer_phone)}</p>
                                                        {customerInfo[booking.booking_id].customer_phone && (
                                                          <p><strong>Phone:</strong> {customerInfo[booking.booking_id].customer_phone}</p>
                                                        )}
                                                        {booking.created_at && (
                                                          <p><strong>Booked On:</strong> {new Date(booking.created_at).toLocaleString()}</p>
                                                        )}
                                                      </>
                                                    ) : (
                                                      <p style={{ color: '#777', fontStyle: 'italic' }}>Loading customer details...</p>
                                                    )}
                                                  </div>
                                                </>
                                              ) : (
                                                <>
                                                  <div className="detail-section" style={{ backgroundColor: '#fff3cd', padding: '10px 14px', borderRadius: '6px', border: '1px solid #ffeeba' }}>
                                                    <h4 style={{ color: '#856404', marginBottom: '8px', fontSize: '1rem', fontWeight: '600' }}>⚠️ Driver Shortage</h4>
                                                    <p style={{ color: '#856404', fontSize: '0.95rem', fontWeight: '500', margin: 0 }}>{rec.message}</p>
                                                    {/* Show rejected driver info inline if available */}
                                                    {hasRejected && currentRejected && (
                                                      <div style={{
                                                        marginTop: '8px',
                                                        paddingTop: '8px',
                                                        borderTop: '1px solid #ffeeba'
                                                      }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                          <strong style={{ color: '#e65100', fontSize: '0.95rem' }}>
                                                            🚫 {toggleValue === 'first' ? 'First' : 'Last'} Rejected Driver
                                                          </strong>
                                                          {hasFirstRejected && hasRejected && firstRejectedDrivers[booking.booking_id]?.driver_id !== rejectedDrivers[booking.booking_id]?.driver_id && (
                                                            <div style={{
                                                              display: 'flex',
                                                              borderRadius: '6px',
                                                              overflow: 'hidden',
                                                              border: '1px solid #e65100',
                                                              fontSize: '0.8rem'
                                                            }}>
                                                              <button
                                                                style={{
                                                                  padding: '2px 8px',
                                                                  border: 'none',
                                                                  cursor: 'pointer',
                                                                  backgroundColor: toggleValue === 'first' ? '#e65100' : '#fff',
                                                                  color: toggleValue === 'first' ? '#fff' : '#e65100',
                                                                  fontWeight: 'bold'
                                                                }}
                                                                onClick={() => setRejectedDriverToggle(prev => ({
                                                                  ...prev,
                                                                  [booking.booking_id]: 'first'
                                                                }))}
                                                              >
                                                                First
                                                              </button>
                                                              <button
                                                                style={{
                                                                  padding: '2px 8px',
                                                                  border: 'none',
                                                                  cursor: 'pointer',
                                                                  backgroundColor: toggleValue === 'last' ? '#e65100' : '#fff',
                                                                  color: toggleValue === 'last' ? '#fff' : '#e65100',
                                                                  fontWeight: 'bold'
                                                                }}
                                                                onClick={() => setRejectedDriverToggle(prev => ({
                                                                  ...prev,
                                                                  [booking.booking_id]: 'last'
                                                                }))}
                                                              >
                                                                Last
                                                              </button>
                                                            </div>
                                                          )}
                                                        </div>
                                                        <p style={{ margin: '6px 0', fontSize: '0.95rem', lineHeight: '1.45' }}><strong>Name:</strong> {currentRejected.driver_name} &nbsp;|&nbsp; <strong>Vehicle:</strong> #{currentRejected.vehicle_id}</p>
                                                        {currentRejected.driver_phone && (
                                                          <p style={{ margin: '6px 0', fontSize: '0.95rem', lineHeight: '1.45' }}>
                                                            <strong>Phone:</strong> {currentRejected.driver_phone}
                                                            <button
                                                              style={{
                                                                marginLeft: '6px',
                                                                padding: '3px 8px',
                                                                backgroundColor: '#e65100',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '3px',
                                                                cursor: 'pointer',
                                                                fontSize: '0.85rem',
                                                                fontWeight: 'bold'
                                                              }}
                                                              onClick={() => {
                                                                const phone = currentRejected.driver_phone;
                                                                if (phone) {
                                                                  window.open(`tel:${phone}`);
                                                                } else {
                                                                  alert('Driver phone number not available');
                                                                }
                                                              }}
                                                            >
                                                              📞 Call
                                                            </button>
                                                          </p>
                                                        )}
                                                        {currentRejected.rejected_at && (
                                                          <p style={{ margin: '4px 0 0 0', fontSize: '0.88rem', color: '#666', lineHeight: '1.45' }}>
                                                            <strong>Rejected At:</strong> {new Date(currentRejected.rejected_at).toLocaleTimeString()}
                                                          </p>
                                                        )}
                                                      </div>
                                                    )}
                                                  </div>
                                                  <div className="detail-section" style={{
                                                    backgroundColor: '#e8f5e9',
                                                    border: '1px solid #a5d6a7',
                                                    borderRadius: '6px',
                                                    padding: '10px 14px'
                                                  }}>
                                                    <h4 style={{ color: '#2e7d32', margin: '0 0 10px 0', borderBottom: '1px solid #2e7d32', paddingBottom: '6px', fontSize: '1rem', fontWeight: '600' }}>👤 Customer Details</h4>
                                                    {customerInfo[booking.booking_id] ? (
                                                      <>
                                                        <p style={{ margin: '8px 0', fontSize: '0.95rem', lineHeight: '1.45' }}><strong>Name:</strong> {getCustomerDisplayName(customerInfo[booking.booking_id].customer_name, customerInfo[booking.booking_id].customer_business_name, customerInfo[booking.booking_id].customer_phone)}</p>
                                                        {customerInfo[booking.booking_id].customer_phone && (
                                                          <p style={{ margin: '8px 0', fontSize: '0.95rem', lineHeight: '1.45' }}>
                                                            <strong>Phone:</strong> {customerInfo[booking.booking_id].customer_phone}
                                                            <button
                                                              style={{
                                                                marginLeft: '8px',
                                                                padding: '4px 10px',
                                                                backgroundColor: '#2e7d32',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '3px',
                                                                cursor: 'pointer',
                                                                fontSize: '0.85rem',
                                                                fontWeight: 'bold'
                                                              }}
                                                              onClick={() => window.open(`tel:${customerInfo[booking.booking_id].customer_phone}`)}
                                                            >
                                                              📞 Call
                                                            </button>
                                                          </p>
                                                        )}
                                                      </>
                                                    ) : (
                                                      <p style={{ color: '#777', fontStyle: 'italic', fontSize: '0.95rem' }}>Loading customer details...</p>
                                                    )}
                                                  </div>
                                                </>
                                              )
                                            ) : rec.type === 'customer_side' ? (
                                              <>
                                                <div className="detail-section">
                                                  <h4>🟠 Ops Agent Analysis (Scheduled Booking)</h4>
                                                  <p><strong>Booking Time:</strong> {new Date(rec.pickup_time).toLocaleString()}</p>
                                                  <p><strong>Trip Distance:</strong> {rec.distance} km</p>
                                                  <p>
                                                    <strong>Dead KM (Driver → Pickup):</strong> {rec.dead_km} km
                                                  </p>
                                                  {rec.driver_location_name && (
                                                    <p style={{ fontSize: '0.85em', color: '#333', margin: '4px 0 0 0' }}>
                                                      <span style={{ color: '#e74c3c' }}>📍</span> <span style={{ textTransform: 'capitalize' }}>{rec.driver_location_name}</span>
                                                    </p>
                                                  )}
                                                </div>
                                                <div className="detail-section">
                                                  <h4>🚗 Vehicle Details</h4>
                                                  <p><strong>Vehicle:</strong> #{rec.vehicle_id}</p>
                                                  <p><strong>Class:</strong> {rec.driver_class || rec.vehicle_class}</p>
                                                  {rec.idle_since && (
                                                    <p style={{ fontSize: '0.85em', color: '#666' }}>
                                                      <strong>Idle Since:</strong> {new Date(rec.idle_since).toLocaleTimeString()}
                                                    </p>
                                                  )}
                                                  <p style={{ fontSize: '0.85em', color: '#888', marginTop: '6px' }}>
                                                    {rec.message}
                                                  </p>
                                                </div>
                                              </>
                                            ) : (
                                              <>
                                                <div className="detail-section">
                                                  <h4>{rec.profitable === false ? '⚠️ Ops Agent Analysis (Not Profitable)' : '🤖 Ops Agent Analysis'}</h4>
                                                  <p><strong>Class:</strong> {rec.vehicle_class} | <strong>Distance:</strong> {rec.distance} km</p>
                                                  <p>
                                                    <strong>Booking Type:</strong> <span className="ops-inline-badge">{rec.booking_type}</span>
                                                    {rec.initial_dead_km !== undefined && (
                                                      <> | <strong>Dead KM:</strong> {rec.initial_dead_km} km</>
                                                    )}
                                                  </p>
                                                  {rec.driver_location_name && (
                                                    <p style={{ fontSize: '0.85em', color: '#333', margin: '4px 0 0 0' }}>
                                                      <span style={{ color: '#e74c3c' }}>📍</span> <span style={{ textTransform: 'capitalize' }}>{rec.driver_location_name}</span>
                                                    </p>
                                                  )}
                                                  {rec.eta_minutes !== undefined && (
                                                    <p style={{ fontSize: '0.9em', color: '#1565c0', fontWeight: '600', margin: '6px 0 0 0' }}>
                                                      🕐 Driver needs ~{rec.eta_minutes} min to reach pickup
                                                    </p>
                                                  )}
                                                  {rec.pickup_time_passed && (
                                                    <p style={{ fontSize: '0.9em', color: '#d32f2f', fontWeight: '600', margin: '4px 0 0 0' }}>
                                                      ⚠️ Pickup time has already passed
                                                    </p>
                                                  )}
                                                </div>
                                                <div className="detail-section">
                                                  <h4>🚗 Vehicle Details</h4>
                                                  <p><strong>Vehicle:</strong> #{rec.vehicle_id}</p>
                                                  <div className="ops-card-financials" style={{ marginTop: '8px' }}>
                                                    <span>Pay: ₹{rec.driver_pay?.toLocaleString()}</span>
                                                    <span>Fare: ₹{rec.customer_fare?.toLocaleString()}</span>
                                                    <span className={rec.margin_pct >= 0 ? 'ops-profit' : (rec.margin_pct >= -10 ? 'ops-marginal' : 'ops-loss')}>
                                                      {rec.margin_pct >= 0 ? '+' : ''}{rec.margin_pct}% {rec.margin_pct >= -10 ? '✅' : (rec.profitable === false ? '❌' : '⚠️')}
                                                    </span>
                                                  </div>
                                                </div>
                                                {/* Rejected Driver info (shown inline in driver_side cards) */}
                                                {hasRejected && currentRejected && (
                                                  <div className="detail-section" style={{
                                                    backgroundColor: '#fff3e0',
                                                    border: '1px solid #ffcc80',
                                                    borderRadius: '6px',
                                                    padding: '10px 14px',
                                                    marginTop: '0'
                                                  }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                                                      <strong style={{ color: '#e65100', fontSize: '0.95rem' }}>
                                                        🚫 {toggleValue === 'first' ? 'First' : 'Last'} Rejected Driver
                                                      </strong>
                                                      {hasFirstRejected && hasRejected && firstRejectedDrivers[booking.booking_id]?.driver_id !== rejectedDrivers[booking.booking_id]?.driver_id && (
                                                        <div style={{
                                                          display: 'flex',
                                                          borderRadius: '6px',
                                                          overflow: 'hidden',
                                                          border: '1px solid #e65100',
                                                          fontSize: '0.8rem'
                                                        }}>
                                                          <button
                                                            style={{
                                                              padding: '2px 8px',
                                                              border: 'none',
                                                              cursor: 'pointer',
                                                              backgroundColor: toggleValue === 'first' ? '#e65100' : '#fff',
                                                              color: toggleValue === 'first' ? '#fff' : '#e65100',
                                                              fontWeight: 'bold'
                                                            }}
                                                            onClick={() => setRejectedDriverToggle(prev => ({
                                                              ...prev,
                                                              [booking.booking_id]: 'first'
                                                            }))}
                                                          >
                                                            First
                                                          </button>
                                                          <button
                                                            style={{
                                                              padding: '2px 8px',
                                                              border: 'none',
                                                              cursor: 'pointer',
                                                              backgroundColor: toggleValue === 'last' ? '#e65100' : '#fff',
                                                              color: toggleValue === 'last' ? '#fff' : '#e65100',
                                                              fontWeight: 'bold'
                                                            }}
                                                            onClick={() => setRejectedDriverToggle(prev => ({
                                                              ...prev,
                                                              [booking.booking_id]: 'last'
                                                            }))}
                                                          >
                                                            Last
                                                          </button>
                                                        </div>
                                                      )}
                                                    </div>
                                                    <p style={{ margin: '6px 0', fontSize: '0.95rem', lineHeight: '1.45' }}><strong>Name:</strong> {currentRejected.driver_name} &nbsp;|&nbsp; <strong>Vehicle:</strong> #{currentRejected.vehicle_id}</p>
                                                    {currentRejected.driver_phone && (
                                                      <p style={{ margin: '6px 0', fontSize: '0.95rem', lineHeight: '1.45' }}>
                                                        <strong>Phone:</strong> {currentRejected.driver_phone}
                                                        <button
                                                          style={{
                                                            marginLeft: '6px',
                                                            padding: '3px 8px',
                                                            backgroundColor: '#e65100',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '3px',
                                                            cursor: 'pointer',
                                                            fontSize: '0.85rem',
                                                            fontWeight: 'bold'
                                                          }}
                                                          onClick={() => {
                                                            const phone = currentRejected.driver_phone;
                                                            if (phone) {
                                                              window.open(`tel:${phone}`);
                                                            } else {
                                                              alert('Driver phone number not available');
                                                            }
                                                          }}
                                                        >
                                                          📞 Call
                                                        </button>
                                                      </p>
                                                    )}
                                                    {currentRejected.rejected_at && (
                                                      <p style={{ margin: '4px 0 0 0', fontSize: '0.88rem', color: '#666', lineHeight: '1.45' }}>
                                                        <strong>Rejected At:</strong> {new Date(currentRejected.rejected_at).toLocaleTimeString()}
                                                      </p>
                                                    )}
                                                  </div>
                                                )}
                                              </>
                                            )}
                                          </div>
                                          <div className="details-actions">
                                            {rec.type === 'no_drivers_available' ? (
                                              <>
                                                <button
                                                  className="btn-onboard"
                                                  style={{ backgroundColor: '#007bff', padding: '8px 16px', fontSize: '1em' }}
                                                  onClick={() => window.open('/#/app/vehicles/new', '_blank')}
                                                >
                                                  ➕ Onboard Vehicle
                                                </button>
                                                <button
                                                  className="btn-remove"
                                                  onClick={() => handleRemoveBooking(booking.booking_id)}
                                                >
                                                  Remove
                                                </button>
                                              </>
                                            ) : rec.type === 'customer_side' ? (
                                              <>
                                                <button
                                                  className="btn-assign"
                                                  style={{ backgroundColor: '#28a745' }}
                                                  onClick={() => {
                                                    if (rec.customer_phone) {
                                                      window.open(`tel:${rec.customer_phone}`);
                                                    } else {
                                                      alert('Customer phone number not available');
                                                    }
                                                  }}
                                                >
                                                  📞 Call
                                                </button>
                                                <button
                                                  className="btn-onboard"
                                                  style={{ backgroundColor: '#007bff' }}
                                                  onClick={() => handleContactCustomer(
                                                    rec.booking_id,
                                                    rec.customer_id,
                                                    rec.customer_phone
                                                  )}
                                                >
                                                  💬 Message
                                                </button>
                                                <button
                                                  className="btn-remove"
                                                  onClick={() => handleRemoveBooking(booking.booking_id)}
                                                >
                                                  Remove
                                                </button>
                                              </>
                                            ) : (
                                              <>
                                                <button
                                                  className="btn-assign"
                                                  onClick={() => {
                                                    setSelectedBookingId(rec.booking_id);
                                                    setAssignMode('regular');
                                                    setVehicleId(String(rec.vehicle_id || ''));
                                                    setShowAssignModal(true);
                                                  }}
                                                >
                                                  Assign Driver
                                                </button>
                                                <button
                                                  className="btn-onboard"
                                                  style={rec.profitable === false ? { backgroundColor: '#e53935' } : {}}
                                                  onClick={() => window.open('/#/app/vehicles/new', '_blank')}
                                                >
                                                  Onboard Vehicle
                                                </button>
                                                <button
                                                  className="btn-remove"
                                                  onClick={() => handleRemoveBooking(booking.booking_id)}
                                                >
                                                  Remove
                                                </button>
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}

                                  {/* Data-only panel: Rejected Driver (with toggle) + Customer + Zone Drivers */}
                                  {!hasRecs && (
                                    <div className="details-container" style={{ borderTop: 'none' }}>
                                      <div className="details-content">
                                        {/* Rejected Driver card with First/Last toggle */}
                                        {hasRejected && currentRejected && (
                                          <div className="detail-section" style={{
                                            backgroundColor: '#fff3e0',
                                            border: '1px solid #ffcc80',
                                            borderRadius: '4px',
                                            padding: '6px 10px'
                                          }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px', borderBottom: '1px solid #e65100', paddingBottom: '3px' }}>
                                              <h4 style={{ color: '#e65100', margin: 0, borderBottom: 'none', fontSize: '0.85em', fontWeight: '600' }}>
                                                🚫 {toggleValue === 'first' ? 'First' : 'Last'} Rejected Driver
                                              </h4>
                                              {hasFirstRejected && hasRejected && firstRejectedDrivers[booking.booking_id]?.driver_id !== rejectedDrivers[booking.booking_id]?.driver_id && (
                                                <div style={{
                                                  display: 'flex',
                                                  borderRadius: '6px',
                                                  overflow: 'hidden',
                                                  border: '1px solid #e65100',
                                                  fontSize: '0.75em'
                                                }}>
                                                  <button
                                                    style={{
                                                      padding: '3px 10px',
                                                      border: 'none',
                                                      cursor: 'pointer',
                                                      backgroundColor: toggleValue === 'first' ? '#e65100' : '#fff',
                                                      color: toggleValue === 'first' ? '#fff' : '#e65100',
                                                      fontWeight: 'bold'
                                                    }}
                                                    onClick={() => setRejectedDriverToggle(prev => ({
                                                      ...prev,
                                                      [booking.booking_id]: 'first'
                                                    }))}
                                                  >
                                                    First
                                                  </button>
                                                  <button
                                                    style={{
                                                      padding: '3px 10px',
                                                      border: 'none',
                                                      cursor: 'pointer',
                                                      backgroundColor: toggleValue === 'last' ? '#e65100' : '#fff',
                                                      color: toggleValue === 'last' ? '#fff' : '#e65100',
                                                      fontWeight: 'bold'
                                                    }}
                                                    onClick={() => setRejectedDriverToggle(prev => ({
                                                      ...prev,
                                                      [booking.booking_id]: 'last'
                                                    }))}
                                                  >
                                                    Last
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                            <p style={{ margin: '3px 0', fontSize: '0.8em', lineHeight: '1.4' }}><strong>Name:</strong> {currentRejected.driver_name}</p>
                                            {currentRejected.driver_phone && (
                                              <p style={{ margin: '3px 0', fontSize: '0.8em', lineHeight: '1.4' }}><strong>Phone:</strong> {currentRejected.driver_phone}</p>
                                            )}
                                            <p style={{ margin: '3px 0', fontSize: '0.8em', lineHeight: '1.4' }}><strong>Vehicle:</strong> #{currentRejected.vehicle_id}</p>
                                            {currentRejected.rejected_at && (
                                              <p style={{ margin: '3px 0', fontSize: '0.75em', color: '#666', lineHeight: '1.4' }}>
                                                <strong>Rejected At:</strong> {new Date(currentRejected.rejected_at).toLocaleTimeString()}
                                              </p>
                                            )}
                                            <button
                                              style={{
                                                marginTop: '5px',
                                                padding: '3px 10px',
                                                backgroundColor: '#e65100',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '3px',
                                                cursor: 'pointer',
                                                fontSize: '0.75em',
                                                fontWeight: 'bold'
                                              }}
                                              onClick={() => {
                                                const phone = currentRejected.driver_phone;
                                                if (phone) {
                                                  window.open(`tel:${phone}`);
                                                } else {
                                                  alert('Driver phone number not available');
                                                }
                                              }}
                                            >
                                              📞 Call Driver
                                            </button>
                                          </div>
                                        )}

                                        {/* Customer info */}
                                        {customerInfo[booking.booking_id] && (
                                          <div className="detail-section" style={{
                                            backgroundColor: '#e8f5e9',
                                            border: '1px solid #a5d6a7',
                                            borderRadius: '4px',
                                            padding: '6px 10px'
                                          }}>
                                            <h4 style={{ color: '#2e7d32', margin: '0 0 4px 0', borderBottom: '1px solid #2e7d32', paddingBottom: '3px', fontSize: '0.85em', fontWeight: '600' }}>👤 Customer</h4>
                                            <p style={{ margin: '3px 0', fontSize: '0.8em', lineHeight: '1.4' }}><strong>Name:</strong> {getCustomerDisplayName(customerInfo[booking.booking_id].customer_name, customerInfo[booking.booking_id].customer_business_name, customerInfo[booking.booking_id].customer_phone)}</p>
                                            {customerInfo[booking.booking_id].customer_phone && (
                                              <p style={{ margin: '3px 0', fontSize: '0.8em', lineHeight: '1.4' }}><strong>Phone:</strong> {customerInfo[booking.booking_id].customer_phone}</p>
                                            )}
                                            <button
                                              style={{
                                                marginTop: '5px',
                                                padding: '3px 10px',
                                                backgroundColor: '#2e7d32',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '3px',
                                                cursor: 'pointer',
                                                fontSize: '0.75em',
                                                fontWeight: 'bold'
                                              }}
                                              onClick={() => {
                                                const phone = customerInfo[booking.booking_id].customer_phone;
                                                if (phone) {
                                                  window.open(`tel:${phone}`);
                                                } else {
                                                  alert('Customer phone number not available');
                                                }
                                              }}
                                            >
                                              📞 Call Customer
                                            </button>
                                          </div>
                                        )}

                                        {/* Higher-Class Zone Drivers */}
                                        {hasZone && (
                                          <div className="detail-section" style={{
                                            backgroundColor: '#e3f2fd',
                                            border: '1px solid #90caf9',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            flexBasis: '100%'
                                          }}>
                                            <h4
                                              style={{
                                                color: '#1565c0',
                                                margin: '0',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                userSelect: 'none'
                                              }}
                                              onClick={() => setExpandedZoneDrivers(prev => ({
                                                ...prev,
                                                [`ops-${booking.booking_id}`]: !prev[`ops-${booking.booking_id}`]
                                              }))}
                                            >
                                              <span>🚗 Higher-Class Drivers Nearby ({zoneDrivers[booking.booking_id].length})</span>
                                              <span style={{ fontSize: '0.8em' }}>{expandedZoneDrivers[`ops-${booking.booking_id}`] ? '▲' : '▼'}</span>
                                            </h4>
                                            {expandedZoneDrivers[`ops-${booking.booking_id}`] && (
                                              <table style={{
                                                width: '100%',
                                                marginTop: '10px',
                                                borderCollapse: 'collapse',
                                                fontSize: '0.9em'
                                              }}>
                                                <thead>
                                                  <tr style={{ backgroundColor: '#bbdefb', textAlign: 'left' }}>
                                                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #90caf9' }}>Vehicle</th>
                                                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #90caf9' }}>Name</th>
                                                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #90caf9' }}>Class</th>
                                                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #90caf9' }}>Dist</th>
                                                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #90caf9' }}>D.Pay</th>
                                                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #90caf9' }}>C.Pay</th>
                                                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #90caf9' }}>Margin</th>
                                                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #90caf9' }}>Action</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {zoneDrivers[booking.booking_id].map((driver, idx) => {
                                                    const marginColor = driver.margin_pct == null ? '#888' : driver.margin_pct >= 10 ? '#2e7d32' : driver.margin_pct >= 0 ? '#f57c00' : '#c62828';
                                                    return (
                                                    <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#e3f2fd' : '#fff' }}>
                                                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>#{driver.vehicle_id || driver.driver_id}</td>
                                                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>{driver.driver_name}</td>
                                                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Class {driver.vehicle_class}</td>
                                                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>{driver.distance_km} km</td>
                                                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>
                                                        {driver.driver_pay != null ? `₹${driver.driver_pay}` : '—'}
                                                      </td>
                                                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>
                                                        {driver.customer_fare != null ? `₹${driver.customer_fare}` : '—'}
                                                      </td>
                                                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #e0e0e0', color: marginColor, fontWeight: 'bold' }}>
                                                        {driver.margin_pct != null ? `${driver.margin_pct > 0 ? '+' : ''}${driver.margin_pct}%` : '—'}
                                                      </td>
                                                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>
                                                        <button
                                                          style={{
                                                            padding: '4px 12px',
                                                            backgroundColor: '#2e7d32',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '0.85em',
                                                            fontWeight: 'bold'
                                                          }}
                                                          onClick={() => {
                                                            setSelectedBookingId(booking.booking_id);
                                                            setVehicleId(String(driver.vehicle_id || ''));
                                                            setShowAssignModal(true);
                                                          }}
                                                        >
                                                          Assign
                                                        </button>
                                                      </td>
                                                    </tr>
                                                    );
                                                  })}
                                                </tbody>
                                              </table>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                      <div className="details-actions">
                                        <button
                                          className="btn-assign"
                                          onClick={() => handleAssignManually(booking.booking_id)}
                                        >
                                          Assign Manually
                                        </button>
                                        <button
                                          className="btn-contact"
                                          onClick={() => handleContactCustomer(
                                            booking.booking_id,
                                            booking.customer_id,
                                            booking.customer_phone
                                          )}
                                        >
                                          Contact Customer
                                        </button>
                                        <button
                                          className="btn-remove"
                                          onClick={() => handleRemoveBooking(booking.booking_id)}
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      )
                      }
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
      </div>

      {/* Manual Assignment Modal with Two-Step Validation */}
      {
        showAssignModal && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1200
          }}>
            <div className="modal-content" style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              minWidth: '450px',
              maxWidth: '550px',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}>
              <h3 style={{ marginBottom: '16px', color: '#333' }}>
                {assignMode === 'hourly' ? 'Hourly Immediate Assignment' : 'Manual Assignment'}
              </h3>
              <p style={{ marginBottom: '16px', color: '#666' }}>
                {assignMode === 'hourly'
                  ? `Assign hourly booking #${selectedBookingId} immediately`
                  : `Assign booking #${selectedBookingId} to a vehicle`}
              </p>

              {/* Step 1: Input Vehicle ID */}
              {validationStep === 'input' && (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: 'bold',
                      color: '#333'
                    }}>
                      Vehicle ID:
                    </label>
                    <input
                      type="number"
                      value={vehicleId}
                      onChange={(e) => setVehicleId(e.target.value)}
                      placeholder="Enter vehicle ID (e.g., 21)"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                      disabled={isValidating}
                    />
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px'
                  }}>
                    <button
                      onClick={handleCancelAssignment}
                      disabled={isValidating}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: '#f8f9fa',
                        color: '#333',
                        cursor: isValidating ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleValidateAssignment}
                      disabled={(assignMode === 'hourly' ? isAssigning : isValidating) || !vehicleId.trim()}
                      style={{
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: '4px',
                        backgroundColor: ((assignMode === 'hourly' ? isAssigning : isValidating) || !vehicleId.trim()) ? '#ccc' : '#007bff',
                        color: 'white',
                        cursor: ((assignMode === 'hourly' ? isAssigning : isValidating) || !vehicleId.trim()) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {assignMode === 'hourly'
                        ? (isAssigning ? 'Assigning...' : 'Assign Now')
                        : (isValidating ? 'Validating...' : 'Validate & Continue')}
                    </button>
                  </div>
                </>
              )}

              {/* Step 2: Show Warnings */}
              {validationStep === 'warnings' && validationResult && (
                <>
                  {/* Hard Block - Cannot Proceed */}
                  {!validationResult.can_proceed && (
                    <div style={{
                      backgroundColor: '#f8d7da',
                      border: '1px solid #f5c6cb',
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '20px'
                    }}>
                      <h4 style={{ color: '#721c24', margin: '0 0 8px 0' }}>
                        ❌ Assignment Blocked
                      </h4>
                      <p style={{ color: '#721c24', margin: 0 }}>
                        {validationResult.hard_block}
                      </p>
                    </div>
                  )}

                  {/* Warnings List */}
                  {validationResult.warnings && validationResult.warnings.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{ marginBottom: '12px', color: '#333' }}>
                        ⚠️ Warnings ({validationResult.warnings.length})
                      </h4>
                      {validationResult.warnings.map((warning, index) => (
                        <div
                          key={index}
                          style={{
                            backgroundColor: getSeverityBgColor(warning.severity),
                            borderLeft: `4px solid ${getSeverityColor(warning.severity)}`,
                            padding: '12px',
                            marginBottom: '8px',
                            borderRadius: '4px'
                          }}
                        >
                          <div style={{
                            fontWeight: 'bold',
                            color: getSeverityColor(warning.severity),
                            marginBottom: '4px',
                            textTransform: 'uppercase',
                            fontSize: '12px'
                          }}>
                            {warning.severity}
                          </div>
                          <div style={{ color: '#333' }}>
                            {warning.message}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Booking & Vehicle Info */}
                  {validationResult.booking_info && (
                    <div style={{
                      backgroundColor: '#f8f9fa',
                      padding: '12px',
                      borderRadius: '4px',
                      marginBottom: '20px',
                      fontSize: '14px'
                    }}>
                      <div><strong>Booking:</strong> #{validationResult.booking_info.booking_id}</div>
                      <div><strong>Vehicle Type Required:</strong> {validationResult.booking_info.vehicle_type || 'N/A'}</div>
                      <div><strong>Target Vehicle:</strong> #{validationResult.vehicle_info?.vehicle_id} ({validationResult.vehicle_info?.vehicle_type || 'N/A'})</div>
                    </div>
                  )}

                  {/* Acknowledgment Checkbox (only if can proceed with warnings) */}
                  {validationResult.can_proceed && validationResult.warnings?.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={warningsAcknowledged}
                          onChange={(e) => setWarningsAcknowledged(e.target.checked)}
                          style={{ width: '18px', height: '18px' }}
                        />
                        <span style={{ color: '#333' }}>
                          I acknowledge the warnings and want to proceed with this assignment
                        </span>
                      </label>
                    </div>
                  )}

                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px'
                  }}>
                    <button
                      onClick={() => {
                        setValidationStep('input');
                        setValidationResult(null);
                        setWarningsAcknowledged(false);
                      }}
                      disabled={isAssigning}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: '#f8f9fa',
                        color: '#333',
                        cursor: isAssigning ? 'not-allowed' : 'pointer'
                      }}
                    >
                      ← Back
                    </button>
                    <button
                      onClick={handleCancelAssignment}
                      disabled={isAssigning}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: '#f8f9fa',
                        color: '#333',
                        cursor: isAssigning ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    {validationResult.can_proceed && (
                      <button
                        onClick={() => handleConfirmAssignment()}
                        disabled={isAssigning || (validationResult.warnings?.length > 0 && !warningsAcknowledged)}
                        style={{
                          padding: '8px 16px',
                          border: 'none',
                          borderRadius: '4px',
                          backgroundColor: (isAssigning || (validationResult.warnings?.length > 0 && !warningsAcknowledged))
                            ? '#ccc'
                            : (validationResult.warnings?.some(w => w.severity === 'critical') ? '#dc3545' : '#28a745'),
                          color: 'white',
                          cursor: (isAssigning || (validationResult.warnings?.length > 0 && !warningsAcknowledged))
                            ? 'not-allowed'
                            : 'pointer'
                        }}
                      >
                        {isAssigning ? 'Assigning...' :
                          validationResult.warnings?.some(w => w.severity === 'critical')
                            ? '⚠️ Confirm Despite Warnings'
                            : '✓ Confirm Assignment'}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )
      }

      {/* Contact Customer Modal — Call / Text chooser */}
      {
        showContactModal && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1200
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '28px',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              minWidth: '380px',
              maxWidth: '420px',
              textAlign: 'center'
            }}>
              <h3 style={{ marginBottom: '8px', color: '#333', fontSize: '18px' }}>
                Contact Customer
              </h3>
              <p style={{ marginBottom: '6px', color: '#666', fontSize: '14px' }}>
                Booking #{contactBookingId}
              </p>
              {contactCustomerPhone && (
                <p style={{ marginBottom: '20px', color: '#888', fontSize: '13px' }}>
                  📞 {contactCustomerPhone}
                </p>
              )}

              <div style={{
                display: 'flex',
                gap: '16px',
                justifyContent: 'center',
                marginBottom: '16px'
              }}>
                {/* Message Button */}
                <button
                  onClick={handleTextCustomer}
                  disabled={!contactCustomerId}
                  style={{
                    padding: '16px 40px',
                    border: 'none',
                    borderRadius: '10px',
                    backgroundColor: contactCustomerId ? '#007bff' : '#ccc',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: contactCustomerId ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'transform 0.1s, box-shadow 0.1s',
                    boxShadow: contactCustomerId ? '0 2px 8px rgba(0,123,255,0.3)' : 'none'
                  }}
                  onMouseOver={(e) => {
                    if (contactCustomerId) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,123,255,0.4)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (contactCustomerId) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,123,255,0.3)';
                    }
                  }}
                >
                  <span style={{ fontSize: '28px' }}>💬</span>
                  <span>Message</span>
                </button>
              </div>

              <button
                onClick={handleCloseContactModal}
                style={{
                  padding: '8px 24px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  backgroundColor: '#f8f9fa',
                  color: '#666',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )
      }

      {/* Start Conversation Modal — opened when Text is clicked */}
      <StartConversationModal
        isOpen={showConversationModal}
        toggle={() => {
          setShowConversationModal(false);
          setContactCustomerId(null);
          setContactCustomerPhone(null);
          setContactBookingId(null);
        }}
        customerId={contactCustomerId}
      />
    </div >
  );
};

export default EscalationDashboard;